import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { useQuery } from "@apollo/client/react";

import {
  AssistantScope,
  type DocumentStatusEnum,
  ProjectReportPagesDocument,
} from "@app_schema";

import { useFillHeight } from "@application/hooks/use_fill_height";
import { useIsWide } from "@application/hooks/use_is_wide";

import { NotesHeader } from "@styled/assistant_notes";
import { AssistantSplit, AssistantSplitSide } from "@styled/assistant_split";
import { ReportPagesColumn } from "@styled/report_pages_bar";
import { PaneSticky } from "@styled/review_pane";
import { SplitToolbar, ToolbarRow } from "@styled/split_toolbar";

import { RecordPageView } from "../../runs/report_review/record_page_view";
import { assistantLine } from "./assistant_line";
import { AssistantNotesView } from "./assistant_notes_view";
import { AssistantPane } from "./assistant_pane";
import { AssistantPaneSwitch } from "./assistant_pane_switch";
import { AssistantToolbar } from "./assistant_toolbar";
import { ReportFileBar } from "./report_file_bar";
import { ReportPages } from "./report_pages";
import { useAssistantChats } from "./use_assistant_chats";
import { useAssistantNotes } from "./use_assistant_notes";
import { useDraftSections } from "./use_draft_sections";
import { useDraftView } from "./use_draft_view";
import { useDraftingPane } from "./use_drafting_pane";
import {
  DraftingSectionsContext,
  useDraftingSections,
} from "./use_drafting_sections";
import { useEscape } from "./use_escape";
import {
  followedReportFile,
  useReportPageTarget,
} from "./use_report_page_target";
import { useVisiblePage } from "./use_visible_page";

// The Final Report (or, on Drafting, the clinician's draft) with its
// assistant: the chat on the left, the report's pages on the right. A page tag
// switches to its file, then scrolls to it. Until the report is processed, the
// right half is the upload pane. On Drafting the pages stay where he left them
// while the assistant reads, and its sections show under their pages; a Word
// draft also shows as his document, the sections typed in at their markers.
export const ReportAssistantSplit: FC<{
  projectId: string;
  scope: AssistantScope.Report | AssistantScope.Drafting;
  agentId?: string;
  available: boolean;
  files: ReadonlyArray<{
    id: string;
    name: string;
    status: DocumentStatusEnum;
  }>;
  // His Word draft is still being read.
  reading: boolean;
  uploadPane: ReactNode;
}> = ({ projectId, scope, agentId, available, files, reading, uploadPane }) => {
  const chats = useAssistantChats({ projectId, scope, agentId, open: true });
  const isWide = useIsWide();
  const [showAssistant, setShowAssistant] = useState(true);
  const [fileId, setFileId] = useState(files[0]?.id);
  const { target, recordsPage, openPage, closePage } = useReportPageTarget(
    files,
    setFileId,
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const height = useFillHeight(fillRef, true);

  const { data } = useQuery(ProjectReportPagesDocument, {
    variables: { id: projectId, documentId: fileId ?? "" },
    skip: !available || !fileId,
  });
  const pages = data?.project.document.pages ?? [];
  const drafting = scope === AssistantScope.Drafting;
  const sections = useDraftingSections(projectId, drafting, chats.current?.id);
  const draftSections = useDraftSections({
    drafting: sections,
    chats,
    enabled: drafting,
    filesKey: files.map(({ id, status }) => `${id}:${status}`).join(),
  });
  const visible = useVisiblePage(scrollRef, `${fileId}:${pages.length}`);

  // Follow the assistant into the report file it's reading or searched.
  const { readingNow, searchingNow } = chats.reads;
  const followedFile = followedReportFile(files, [
    readingNow?.documentId,
    searchingNow?.pages?.[0]?.documentId,
  ]);
  useEffect(() => {
    if (followedFile && !drafting) setFileId(followedFile);
  }, [followedFile, drafting]);
  // Drafting chats keep no notes, so the case's notes stay with the Report.
  const notes = useAssistantNotes({
    projectId,
    chats,
    open: !drafting,
  });
  const notesOver = notes.shown && (isWide || !showAssistant);
  const line = assistantLine(chats, notes, draftSections);
  const {
    covering,
    editingTemplate,
    controls,
    fileMenu,
    documentView,
    templateView,
    pagesLine,
    showReport,
  } = useDraftView({
    projectId,
    chats,
    draftSections,
    line,
    paneHidden: !isWide && showAssistant,
    setFileId,
  });

  const draftingPane = useDraftingPane({
    drafting,
    chatId: chats.current?.id ?? null,
    editingTemplate,
    draftSections,
    reading,
    onShowReport: () => {
      showReport();
      if (!isWide) setShowAssistant(false);
    },
  });

  const pageOverChat = target !== null && (!isWide || recordsPage);
  useEscape(pageOverChat, closePage);

  // Drafting asks from his sections, never suggested questions.
  const toolbar = (
    <AssistantToolbar
      chats={chats}
      available={available}
      sections={draftingPane.toolbar}
    />
  );
  const pageSwitch = !isWide && (
    <AssistantPaneSwitch
      other="Pages"
      showingAssistant={showAssistant}
      onChange={setShowAssistant}
    />
  );
  const pagesBar = available && fileId && (
    <ReportFileBar
      files={files}
      fileId={fileId}
      name={data?.project.document.name}
      fileMenu={fileMenu}
      page={covering ? null : visible}
      pageCount={data?.project.document.pageCount ?? pages.length}
      actions={controls}
      onSelect={setFileId}
    />
  );

  return (
    <DraftingSectionsContext value={sections}>
      <AssistantSplit
        fillRef={fillRef}
        height={height}
        toolbar={
          isWide ? (
            <SplitToolbar left={toolbar} right={pagesBar} />
          ) : (
            <ToolbarRow trailing={pageSwitch}>
              {showAssistant ? toolbar : pagesBar}
            </ToolbarRow>
          )
        }
      >
        <AssistantPane
          chats={chats}
          scope={scope}
          available={available}
          agentReady={Boolean(agentId)}
          activePage={target}
          onOpenPage={openPage}
          hidden={!isWide && !showAssistant}
          dimmed={editingTemplate}
          drafting={draftingPane.pane}
          overlay={
            pageOverChat && (
              <RecordPageView
                projectId={projectId}
                target={target}
                backLabel="Back to chat"
                onBack={closePage}
              />
            )
          }
        />
        {notesOver && (
          <ReportPagesColumn background="white">
            <PaneSticky background="white">
              {line}
              <NotesHeader onClose={notes.close} />
            </PaneSticky>
            <AssistantNotesView notes={notes} />
          </ReportPagesColumn>
        )}
        {documentView}
        {templateView}
        {available && fileId ? (
          <ReportPages
            documentId={fileId}
            pages={pages}
            target={recordsPage ? null : target}
            reads={chats.reads}
            line={pagesLine}
            scrollRef={scrollRef}
            hidden={(!isWide && showAssistant) || notesOver || covering}
            follow={!drafting}
          />
        ) : (
          // While his Word file is read, its Report already covers this side.
          !covering &&
          (isWide || !showAssistant) && (
            <AssistantSplitSide>{uploadPane}</AssistantSplitSide>
          )
        )}
      </AssistantSplit>
    </DraftingSectionsContext>
  );
};
